import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, CheckCircle2, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { authApi, auth, setCurrentUser } from "@/lib/api";
import { getApiError } from "@/lib/utils";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) nav("/forgot-password", { replace: true });
  }, [token, nav]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.resetPassword(token, pw);
      toast.success("Password updated — signing you in…");
      // Fetch the current user (cookie is now set by the backend)
      try {
        const me = await authApi.me();
        setCurrentUser(me);
      } catch (meErr) {
        // Non-fatal: cookie may not have propagated yet — the redirect
        // below will re-run AuthProvider.me() and pick it up.
        console.warn("[reset-password] /me after reset failed:", meErr);
      }
      nav("/", { replace: true });
    } catch (err) {
      toast.error(getApiError(err, "Could not reset password"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-nmi-700">
            Choose a new password
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
            Set your new password
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Pick something at least 8 characters long that you haven't used here before.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              New password
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={showPw ? "text" : "password"}
                data-testid="reset-password-new"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-9 py-2.5 text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:text-slate-700"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              Confirm new password
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={showPw ? "text" : "password"}
                data-testid="reset-password-confirm"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
              />
            </div>
          </div>
          {pw && pw2 && pw !== pw2 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700">
              <AlertTriangle size={13} /> Passwords don't match yet
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !pw || pw !== pw2}
            data-testid="reset-password-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-nmi-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-nmi-800 disabled:opacity-50"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Set new password
          </button>
        </form>
      </div>
    </div>
  );
}
